import type { ReactNode } from "react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Box, Button, ListItem, Typography } from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { useGetActionsApps } from "@queries/actions-apps/useGetActionsApps";
import { useGetRelevantContactsList } from "@shared/generated/crm";
import { useGoogleContactsAccess } from "@features/Contacts/hooks/useGoogleContactsAccess";
import { ContactsExtractionConsent } from "@features/Contacts/components/ContactsExtractionConsent";
import { CrmSyncIndicator } from "@features/Contacts/components/CrmSyncIndicator";
import { LeadCard } from "@shared/components/LeadCard";
import type { FocusLeadSignal } from "./focusLeadSignal";
import { mapRelevantContactToFocusLead } from "./focus-relevant-mapper";
import { FocusPriorityCardSkeleton } from "./ChatFocusViewSkeleton";
import {
  Eyebrow,
  Header,
  HeaderTop,
  PriorityCardHeader,
  PriorityCardTitle,
  PriorityList,
  PriorityPeopleIconWrap,
  PriorityTitleGroup,
} from "./ChatFocusView.styles";

type Props = {
  eyebrow?: { icon: ReactNode; label: string };
  title: string;
  subtitle: string;
  onLeadClick: (lead: FocusLeadSignal) => void;
  disabled: boolean;
};

export const EmailSignalsSection = ({
  eyebrow,
  title,
  subtitle,
  onLeadClick,
  disabled,
}: Props) => {
  const connectedAppsQuery = useGetActionsApps();
  const { isConnected: isGoogleConnected, hasAccess } =
    useGoogleContactsAccess();
  const needsConsent = !hasAccess;

  const canFetchPriorityLeads = connectedAppsQuery.isSuccess;

  const relevantContactsQuery = useGetRelevantContactsList(
    { limit: 10 },
    {},
    {
      query: {
        enabled: canFetchPriorityLeads,
        // Backend may return a 501 error body as 200; select normalizes to a safe array
        select: (data) => (Array.isArray(data) ? data : []),
      },
    },
  );

  const leads = useMemo(
    () => (relevantContactsQuery.data ?? []).map(mapRelevantContactToFocusLead),
    [relevantContactsQuery.data],
  );

  const showSkeleton =
    connectedAppsQuery.isLoading ||
    (canFetchPriorityLeads && relevantContactsQuery.isLoading);

  if (connectedAppsQuery.isError) {
    return (
      <Box sx={{ textAlign: "center", py: 2, px: 2 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Something went wrong loading your integrations.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => connectedAppsQuery.refetch()}
        >
          Refresh
        </Button>
      </Box>
    );
  }

  if (showSkeleton) {
    return <FocusPriorityCardSkeleton />;
  }

  return (
    <>
      <PriorityCardHeader>
        {eyebrow && (
          <Header>
            <HeaderTop>
              {eyebrow.icon}
              <Eyebrow variant="overline">{eyebrow.label}</Eyebrow>
            </HeaderTop>
            <CrmSyncIndicator compact syncingLabel="Syncing contacts..." />
          </Header>
        )}
        <PriorityTitleGroup>
          {!eyebrow && (
            <PriorityPeopleIconWrap aria-hidden>
              <PeopleAltIcon fontSize="inherit" />
            </PriorityPeopleIconWrap>
          )}
          <PriorityCardTitle variant="subtitle1" id="focus-priority-heading">
            {title}
          </PriorityCardTitle>
          {!eyebrow && (
            <CrmSyncIndicator compact syncingLabel="Syncing contacts..." />
          )}
        </PriorityTitleGroup>
        <Typography variant="body2">{subtitle}</Typography>
      </PriorityCardHeader>
      {needsConsent ? (
        <ContactsExtractionConsent
          isConnected={isGoogleConnected}
          size="small"
        />
      ) : relevantContactsQuery.isError ? (
        <Box sx={{ textAlign: "center", py: 2, px: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Something went wrong loading contacts signals.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => relevantContactsQuery.refetch()}
          >
            Retry
          </Button>
        </Box>
      ) : leads.length === 0 ? (
        <Typography variant="body2">
          No contacts signals yet. <Link to="/contacts">Go to Contacts</Link>
        </Typography>
      ) : (
        <PriorityList disablePadding>
          {leads.map((lead) => (
            <ListItem key={lead.id} disablePadding>
              <LeadCard
                title={lead.displayName}
                avatar={lead.initials || undefined}
                chips={[
                  { value: lead.intentRole },
                  { label: "Stage", value: lead.leadStage },
                ]}
                signal={lead.signalSummary}
                onClick={() => onLeadClick(lead)}
                disabled={disabled}
              />
            </ListItem>
          ))}
        </PriorityList>
      )}
    </>
  );
};
